import '../scss/components/_ownerreports.scss'
import React, { useState } from 'react'

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const fmt = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`

const roomTypeMap = {
  'single': 'Single',
  'double': 'Double',
  'triple': 'Triple',
  'quad': 'Quad',
  'dorm': 'Dorm'
}

// ─── Shared tile ─────────────────────────────────────────────────────────────

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rpt-stat-tile" style={{ borderTopColor: accent }}>
      <span className="rpt-stat-value" style={{ color: accent }}>{value}</span>
      <span className="rpt-stat-label">{label}</span>
      {sub && <span className="rpt-stat-sub">{sub}</span>}
    </div>
  )
}

// ─── Delay Tab ──────────────────────────────────────────────────────────────

function DelayTab({ selectedYear }) {
  const [statusF, setStatusF] = useState('delayed')
  const [sortCol, setSortCol] = useState('daysOverdue')
  const [sortDir, setSortDir] = useState('desc')

  // Mock delay data per year - showing realistic scenarios
  const delayData = {
    2024: [
      { tenant: 'Pooja Gupta', room: '103', bed: '01', rent: 8000, daysOverdue: 22, reminderCount: 5, lastReminder: '2d ago', status: 'critical', months: ['Jan', 'Feb', 'Mar'] },
      { tenant: 'Arjun Choudhary', room: '102', bed: '01', rent: 7000, daysOverdue: 8, reminderCount: 2, lastReminder: '1d ago', status: 'delayed', months: ['Jan', 'Feb'] },
      { tenant: 'Vikas Rao', room: '201', bed: '02', rent: 6000, daysOverdue: 5, reminderCount: 1, lastReminder: '3d ago', status: 'delayed', months: ['Jan'] },
      { tenant: 'Deepak Joshi', room: '202', bed: '01', rent: 7200, daysOverdue: 3, reminderCount: 1, lastReminder: '1d ago', status: 'delayed', months: ['Feb'] },
    ],
    2025: [
      { tenant: 'Sonia Agarwal', room: '102', bed: '02', rent: 8500, daysOverdue: 12, reminderCount: 3, lastReminder: '1d ago', status: 'delayed', months: ['Jan', 'Feb'] },
      { tenant: 'Divya Menon', room: '201', bed: '02', rent: 7500, daysOverdue: 3, reminderCount: 1, lastReminder: '2d ago', status: 'delayed', months: ['Jan'] },
    ],
    2026: [
      { tenant: 'Rahul Verma', room: '102', bed: '02', rent: 9500, daysOverdue: 15, reminderCount: 4, lastReminder: '1d ago', status: 'critical', months: ['Jan', 'Feb', 'Mar'] },
      { tenant: 'Priya Sharma', room: '103', bed: '01', rent: 11000, daysOverdue: 7, reminderCount: 2, lastReminder: '2d ago', status: 'delayed', months: ['Jan', 'Feb'] },
      { tenant: 'Vikram Raj', room: '301', bed: '03', rent: 8800, daysOverdue: 4, reminderCount: 1, lastReminder: '3d ago', status: 'delayed', months: ['Mar'] },
    ]
  }

  const data = delayData[selectedYear] || []

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(col)
      setSortDir('desc')
    }
  }

  const getStatusBadge = (status) => {
    if (status === 'critical') {
      return <span className="cp-rpt-status cp-rpt-st-rej"><span className="cp-rpt-dot" style={{ background: '#EF4444' }} />Critical</span>
    } else if (status === 'delayed') {
      return <span className="cp-rpt-status cp-rpt-st-open"><span className="cp-rpt-dot" style={{ background: '#F59E0B' }} />Delayed</span>
    } else {
      return <span className="cp-rpt-status cp-rpt-st-res"><span className="cp-rpt-dot" style={{ background: '#22C55E' }} />On Time</span>
    }
  }

  const filtered = data.filter(r => statusF === 'all' || r.status === statusF)
  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortCol]
    let bv = b[sortCol]
    if (typeof av === 'string') av = av.toLowerCase()
    if (typeof bv === 'string') bv = bv.toLowerCase()
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  const delayedData = data.filter(r => r.status === 'delayed' || r.status === 'critical')
  const totalDue = delayedData.reduce((sum, r) => sum + r.rent, 0)
  const delayedCount = data.filter(r => r.status === 'delayed').length
  const criticalCount = data.filter(r => r.status === 'critical').length
  const ontimeCount = data.filter(r => r.status === 'ontime').length
  const totalReminders = delayedData.reduce((sum, r) => sum + r.reminderCount, 0)

  return (
    <div className="cp-rpt-section">
      <div className="rpt-tiles">
        <StatTile label="Total amount due" value={fmt(totalDue)} sub={`${delayedCount + criticalCount} tenants delaying`} accent="#EF4444" />
        <StatTile label="Critical (15+ days)" value={criticalCount} sub="Needs a direct call" accent="#991B1B" />
        <StatTile label="Reminders sent" value={totalReminders} sub="This billing cycle" accent="#6366F1" />
        <StatTile label="On-time tenants" value={ontimeCount} sub="Paid on time" accent="#22C55E" />
      </div>

      <div className="cp-rpt-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="cp-rpt-tbl-head">
          <span className="cp-rpt-card-title" style={{ margin: 0 }}>Tenant Payment Delays - {selectedYear}</span>
          <div className="cp-rpt-filter-group">
              <button 
                key="all" 
                className={`cp-rpt-fpill${statusF === 'all' ? ' on' : ''}`} 
                onClick={() => setStatusF('all')}
              >
                All ({data.length})
              </button>
              <button 
                key="critical" 
                className={`cp-rpt-fpill${statusF === 'critical' ? ' on' : ''}`} 
                onClick={() => setStatusF('critical')}
              >
                Critical ({data.filter(r => r.status === 'critical').length})
              </button>
              <button 
                key="delayed" 
                className={`cp-rpt-fpill${statusF === 'delayed' ? ' on' : ''}`} 
                onClick={() => setStatusF('delayed')}
              >
                Delayed ({data.filter(r => r.status === 'delayed').length})
              </button>
            </div>
        </div>
        <div className="cp-rpt-tbl-scroll">
          <table className="cp-rpt-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('tenant')}>Tenant Name</th>
                <th onClick={() => handleSort('room')}>Room</th>
                <th onClick={() => handleSort('bed')}>Bed</th>
                <th onClick={() => handleSort('rent')}>Rent Amount</th>
                <th onClick={() => handleSort('daysOverdue')}>Days Overdue</th>
                <th>Delayed Months</th>  {/* NEW COLUMN */}
                <th onClick={() => handleSort('reminderCount')}>Reminders Sent</th>
                <th onClick={() => handleSort('lastReminder')}>Last Reminder</th>
                {/* <th onClick={() => handleSort('status')}>Status</th> */}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan="8" className="cp-rpt-empty-row">
                    No tenants match the selected filter.
                  </td>
                </tr>
              ) : (
                sorted.map((r, i) => (
                    <tr key={i}>
                      <td className="cp-rpt-td-bold">{r.tenant}</td>
                      <td>{r.room}</td>
                      <td>{r.bed}</td>
                      <td className="cp-rpt-td-mono">{fmt(r.rent)}</td>
                      <td className="cp-rpt-td-mono" style={{ 
                        color: r.daysOverdue > 15 ? '#991B1B' : r.daysOverdue > 0 ? '#92400E' : '#6B7280',
                        fontWeight: r.daysOverdue > 0 ? 600 : 400
                      }}>
                        {r.daysOverdue > 0 ? `${r.daysOverdue} days` : '—'}
                      </td>
                      <td>  {/* ADD THIS BLOCK */}
                        {r.months && r.months.length > 0 ? (
                          <span className="cp-rpt-chip" style={{ background: '#FEF3C7', color: '#92400E' }}>
                            {r.months.join(', ')}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="cp-rpt-td-mono">{r.reminderCount}</td>
                      <td>{r.lastReminder || '—'}</td>
                      {/* <td>{getStatusBadge(r.status)}</td> */}
                    </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="cp-rpt-tbl-foot">
          {sorted.length} tenant{sorted.length !== 1 ? 's' : ''} · {selectedYear}
        </div>
      </div>
    </div>
  )
}

// ─── Month Wise Revenue Accordion ──────────────────────────────────────────

function RevenueMonthAccordion({ revenueData, selectedYear }) {
  const [expandedYears, setExpandedYears] = useState(() => ({
    [selectedYear]: true
  }))

  const [expandedMonths, setExpandedMonths] = useState(() => ({
    [`${selectedYear}-Jan`]: true
  }))

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }))
  }

  const toggleMonth = (key) => {
    setExpandedMonths(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const availableYears = Object.keys(revenueData)
    .map(Number)
    .sort((a, b) => b - a)

  return (
    <div className="rpt-occupancy-accordion">
      <h3 className="rpt-accordion-title">Month Wise Revenue Collection</h3>
      
      {availableYears.map(year => {
        const yearData = revenueData[year] || {}
        const isYearExpanded = expandedYears[year] || false
        const yearMonths = Object.keys(yearData)
        const totalRevenue = yearMonths.reduce(
          (sum, month) => sum + (yearData[month]?.reduce((s, item) => s + item.collected, 0) || 0),
          0
        )

        return (
          <div key={year} className="rpt-year-accordion">
            <button 
              className="rpt-year-toggle"
              onClick={() => toggleYear(year)}
            >
              <span className="rpt-accordion-chevron">
                {isYearExpanded ? '▼' : '▶'}
              </span>
              <span className="rpt-year-label">{year}</span>
              <span className="rpt-year-count">(₹{totalRevenue.toLocaleString('en-IN')} total)</span>
            </button>

            {isYearExpanded && (
              <div className="rpt-year-content">
                {yearMonths.map(month => {
                  const monthData = yearData[month] || []
                  const monthKey = `${year}-${month}`
                  const isMonthExpanded = expandedMonths[monthKey] || false
                  const monthTotal = monthData.reduce((sum, item) => sum + item.collected, 0)

                  return (
                    <div key={month} className="rpt-month-accordion">
                      <button 
                        className="rpt-month-toggle"
                        onClick={() => toggleMonth(monthKey)}
                      >
                        <span className="rpt-accordion-chevron">
                          {isMonthExpanded ? '▼' : '▶'}
                        </span>
                        <span className="rpt-month-label">{month}</span>
                        <span className="rpt-month-count">(₹{monthTotal.toLocaleString('en-IN')} collected)</span>
                      </button>

                      {isMonthExpanded && (
                        <div className="rpt-month-content">
                          {monthData.length > 0 ? (
                            <table className="rpt-occupancy-table">
                              <thead>
                                <tr>
                                  <th>S.No</th>
                                  <th>Room Number</th>
                                  <th>Bed Number</th>
                                  <th>Room Type</th>
                                  <th>Tenant Name</th>
                                  <th>Rent/Month</th>
                                  <th>Days Occupied</th>
                                  <th>Days Vacant</th>
                                  <th>Collected</th>
                                  <th>Revenue Lost</th>
                                  <th>Payment Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {monthData.map((item, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.roomNumber}</td>
                                    <td>{item.bedNumber}</td>
                                    <td>{roomTypeMap[item.roomType] || item.roomType}</td>
                                    <td>{item.tenantName || '—'}</td>
                                    <td className="cp-rpt-td-mono">{fmt(item.rent)}</td>
                                    <td>{item.daysOccupied}</td>
                                    <td>{item.daysVacant}</td>
                                    <td className="cp-rpt-td-mono" style={{ color: '#166534', fontWeight: 600 }}>
                                      {fmt(item.collected)}
                                    </td>
                                    <td className="cp-rpt-td-mono" style={{ color: '#991B1B' }}>
                                      {item.revenueLost > 0 ? fmt(item.revenueLost) : '—'}
                                    </td>
                                    <td>
                                      <span className={`cp-rpt-status ${item.paymentStatus === 'paid' ? 'cp-rpt-st-res' : item.paymentStatus === 'partial' ? 'cp-rpt-st-open' : 'cp-rpt-st-rej'}`}>
                                        <span className="cp-rpt-dot" style={{ 
                                          background: item.paymentStatus === 'paid' ? '#22C55E' : 
                                                     item.paymentStatus === 'partial' ? '#F59E0B' : '#EF4444' 
                                        }} />
                                        {item.paymentStatus === 'paid' ? 'Paid' : 
                                         item.paymentStatus === 'partial' ? 'Partial' : 'Pending'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="cp-rpt-total-row">
                                  <td colSpan="5" className="cp-rpt-td-bold">Monthly Total</td>
                                  <td className="cp-rpt-td-mono">
                                    <strong>{fmt(monthData.reduce((s, i) => s + i.rent, 0))}</strong>
                                  </td>
                                  <td colSpan="2"></td>
                                  <td className="cp-rpt-td-mono">
                                    <strong style={{ color: '#166534' }}>{fmt(monthTotal)}</strong>
                                  </td>
                                  <td className="cp-rpt-td-mono">
                                    <strong style={{ color: '#991B1B' }}>
                                      {fmt(monthData.reduce((s, i) => s + i.revenueLost, 0))}
                                    </strong>
                                  </td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </table>
                          ) : (
                            <p className="rpt-empty-message">No revenue data for this month</p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATIC DATA FOR EACH YEAR
// ═══════════════════════════════════════════════════════════════════════════════

// 2024 DATA - Full year with more data
const DATA_2024 = {
  'Jan': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Rahul Sharma', rent: 7000, daysOccupied: 31, daysVacant: 0, collected: 7000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Sneha Reddy', rent: 7000, daysOccupied: 31, daysVacant: 0, collected: 7000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Choudhary', rent: 7000, daysOccupied: 31, daysVacant: 0, collected: 7000, revenueLost: 0, paymentStatus: 'partial' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: null, rent: 7000, daysOccupied: 0, daysVacant: 31, collected: 0, revenueLost: 7000, paymentStatus: 'pending' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Pooja Gupta', rent: 8000, daysOccupied: 31, daysVacant: 0, collected: 6000, revenueLost: 2000, paymentStatus: 'partial' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Meena Dasari', rent: 6000, daysOccupied: 31, daysVacant: 0, collected: 6000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Vikas Rao', rent: 6000, daysOccupied: 31, daysVacant: 0, collected: 5000, revenueLost: 1000, paymentStatus: 'partial' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Kiran Menon', rent: 6000, daysOccupied: 31, daysVacant: 0, collected: 6000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Deepak Joshi', rent: 7200, daysOccupied: 31, daysVacant: 0, collected: 7200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: null, rent: 7200, daysOccupied: 0, daysVacant: 31, collected: 0, revenueLost: 7200, paymentStatus: 'pending' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ananya Singh', rent: 8200, daysOccupied: 31, daysVacant: 0, collected: 8200, revenueLost: 0, paymentStatus: 'paid' },
  ],
  'Feb': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Rahul Sharma', rent: 7000, daysOccupied: 28, daysVacant: 0, collected: 7000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Sneha Reddy', rent: 7000, daysOccupied: 28, daysVacant: 0, collected: 7000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Choudhary', rent: 7000, daysOccupied: 28, daysVacant: 0, collected: 7000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: null, rent: 7000, daysOccupied: 0, daysVacant: 28, collected: 0, revenueLost: 7000, paymentStatus: 'pending' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Pooja Gupta', rent: 8000, daysOccupied: 28, daysVacant: 0, collected: 8000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Meena Dasari', rent: 6000, daysOccupied: 28, daysVacant: 0, collected: 6000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Vikas Rao', rent: 6000, daysOccupied: 28, daysVacant: 0, collected: 6000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Kiran Menon', rent: 6000, daysOccupied: 28, daysVacant: 0, collected: 6000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Deepak Joshi', rent: 7200, daysOccupied: 28, daysVacant: 0, collected: 5000, revenueLost: 2200, paymentStatus: 'partial' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: null, rent: 7200, daysOccupied: 0, daysVacant: 28, collected: 0, revenueLost: 7200, paymentStatus: 'pending' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ananya Singh', rent: 8200, daysOccupied: 28, daysVacant: 0, collected: 8200, revenueLost: 0, paymentStatus: 'paid' },
  ],
}

// 2025 DATA - Full year
const DATA_2025 = {
  'Jan': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Suresh Iyer', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Priya Nair', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Naveen Pillai', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Sonia Agarwal', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 7000, revenueLost: 1500, paymentStatus: 'partial' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Ravi Kumar', rent: 10000, daysOccupied: 31, daysVacant: 0, collected: 10000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Verma', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 7500, daysOccupied: 31, daysVacant: 0, collected: 7500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 7500, daysOccupied: 31, daysVacant: 0, collected: 7500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: null, rent: 7500, daysOccupied: 0, daysVacant: 31, collected: 0, revenueLost: 7500, paymentStatus: 'pending' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Vivek Singh', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Aditi Rao', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ishaan Mehta', rent: 10000, daysOccupied: 31, daysVacant: 0, collected: 10000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Ritu Sharma', rent: 7800, daysOccupied: 31, daysVacant: 0, collected: 7800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 7800, daysOccupied: 31, daysVacant: 0, collected: 7800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 7800, daysOccupied: 31, daysVacant: 0, collected: 7800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Vikram Raj', rent: 9000, daysOccupied: 31, daysVacant: 0, collected: 9000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Sara Khan', rent: 9000, daysOccupied: 31, daysVacant: 0, collected: 9000, revenueLost: 0, paymentStatus: 'paid' },
  ],
}

// 2026 DATA - Partial year (Jan-Jun) with all 20 beds occupied
const DATA_2026 = {
  'Jan': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Nair', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Meera Iyer', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Karan Patel', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Rahul Verma', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 8000, revenueLost: 1500, paymentStatus: 'partial' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Priya Sharma', rent: 11000, daysOccupied: 31, daysVacant: 0, collected: 9000, revenueLost: 2000, paymentStatus: 'partial' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Singh', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Vivek Singh', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Aditi Rao', rent: 9800, daysOccupied: 31, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Ishaan Mehta', rent: 9800, daysOccupied: 31, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ritu Sharma', rent: 11200, daysOccupied: 31, daysVacant: 0, collected: 11200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Vikram Raj', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 7000, revenueLost: 1800, paymentStatus: 'partial' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Sara Khan', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Arjun Nair', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '01', roomType: 'double', tenantName: 'Meera Iyer', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '02', roomType: 'double', tenantName: 'Karan Patel', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
  ],
  'Feb': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Nair', rent: 9500, daysOccupied: 28, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Meera Iyer', rent: 9500, daysOccupied: 28, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Karan Patel', rent: 9500, daysOccupied: 28, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Rahul Verma', rent: 9500, daysOccupied: 28, daysVacant: 0, collected: 8000, revenueLost: 1500, paymentStatus: 'partial' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Priya Sharma', rent: 11000, daysOccupied: 28, daysVacant: 0, collected: 9000, revenueLost: 2000, paymentStatus: 'partial' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Singh', rent: 9500, daysOccupied: 28, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 9500, daysOccupied: 28, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 8500, daysOccupied: 28, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 8500, daysOccupied: 28, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Vivek Singh', rent: 8500, daysOccupied: 28, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Aditi Rao', rent: 9800, daysOccupied: 28, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Ishaan Mehta', rent: 9800, daysOccupied: 28, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ritu Sharma', rent: 11200, daysOccupied: 28, daysVacant: 0, collected: 11200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 8800, daysOccupied: 28, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 8800, daysOccupied: 28, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Vikram Raj', rent: 8800, daysOccupied: 28, daysVacant: 0, collected: 7000, revenueLost: 1800, paymentStatus: 'partial' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Sara Khan', rent: 10200, daysOccupied: 28, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Arjun Nair', rent: 10200, daysOccupied: 28, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '01', roomType: 'double', tenantName: 'Meera Iyer', rent: 10200, daysOccupied: 28, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '02', roomType: 'double', tenantName: 'Karan Patel', rent: 10200, daysOccupied: 28, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
  ],
  'Mar': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Nair', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Meera Iyer', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Karan Patel', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Rahul Verma', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 8000, revenueLost: 1500, paymentStatus: 'partial' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Priya Sharma', rent: 11000, daysOccupied: 31, daysVacant: 0, collected: 11000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Singh', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Vivek Singh', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Aditi Rao', rent: 9800, daysOccupied: 31, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Ishaan Mehta', rent: 9800, daysOccupied: 31, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ritu Sharma', rent: 11200, daysOccupied: 31, daysVacant: 0, collected: 11200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Vikram Raj', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 7000, revenueLost: 1800, paymentStatus: 'partial' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Sara Khan', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Arjun Nair', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '01', roomType: 'double', tenantName: 'Meera Iyer', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '02', roomType: 'double', tenantName: 'Karan Patel', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
  ],
  'Apr': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Nair', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Meera Iyer', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Karan Patel', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Rahul Verma', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 8000, revenueLost: 1500, paymentStatus: 'partial' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Priya Sharma', rent: 11000, daysOccupied: 30, daysVacant: 0, collected: 11000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Singh', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 8500, daysOccupied: 30, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 8500, daysOccupied: 30, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Vivek Singh', rent: 8500, daysOccupied: 30, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Aditi Rao', rent: 9800, daysOccupied: 30, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Ishaan Mehta', rent: 9800, daysOccupied: 30, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ritu Sharma', rent: 11200, daysOccupied: 30, daysVacant: 0, collected: 11200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 8800, daysOccupied: 30, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 8800, daysOccupied: 30, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Vikram Raj', rent: 8800, daysOccupied: 30, daysVacant: 0, collected: 7000, revenueLost: 1800, paymentStatus: 'partial' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Sara Khan', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Arjun Nair', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '01', roomType: 'double', tenantName: 'Meera Iyer', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '02', roomType: 'double', tenantName: 'Karan Patel', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
  ],
  'May': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Nair', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Meera Iyer', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Karan Patel', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Rahul Verma', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Priya Sharma', rent: 11000, daysOccupied: 31, daysVacant: 0, collected: 11000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Singh', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 9500, daysOccupied: 31, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Vivek Singh', rent: 8500, daysOccupied: 31, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Aditi Rao', rent: 9800, daysOccupied: 31, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Ishaan Mehta', rent: 9800, daysOccupied: 31, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ritu Sharma', rent: 11200, daysOccupied: 31, daysVacant: 0, collected: 11200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Vikram Raj', rent: 8800, daysOccupied: 31, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Sara Khan', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Arjun Nair', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '01', roomType: 'double', tenantName: 'Meera Iyer', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '02', roomType: 'double', tenantName: 'Karan Patel', rent: 10200, daysOccupied: 31, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
  ],
  'Jun': [
    { roomNumber: '101', bedNumber: '01', roomType: 'double', tenantName: 'Arjun Nair', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '101', bedNumber: '02', roomType: 'double', tenantName: 'Meera Iyer', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '01', roomType: 'double', tenantName: 'Karan Patel', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '102', bedNumber: '02', roomType: 'double', tenantName: 'Rahul Verma', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '103', bedNumber: '01', roomType: 'single', tenantName: 'Priya Sharma', rent: 11000, daysOccupied: 30, daysVacant: 0, collected: 11000, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '01', roomType: 'double', tenantName: 'Amit Singh', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '104', bedNumber: '02', roomType: 'double', tenantName: 'Neha Patel', rent: 9500, daysOccupied: 30, daysVacant: 0, collected: 9500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '01', roomType: 'triple', tenantName: 'Karthik Nair', rent: 8500, daysOccupied: 30, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '02', roomType: 'triple', tenantName: 'Divya Menon', rent: 8500, daysOccupied: 30, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '201', bedNumber: '03', roomType: 'triple', tenantName: 'Vivek Singh', rent: 8500, daysOccupied: 30, daysVacant: 0, collected: 8500, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '01', roomType: 'double', tenantName: 'Aditi Rao', rent: 9800, daysOccupied: 30, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '202', bedNumber: '02', roomType: 'double', tenantName: 'Ishaan Mehta', rent: 9800, daysOccupied: 30, daysVacant: 0, collected: 9800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '203', bedNumber: '01', roomType: 'single', tenantName: 'Ritu Sharma', rent: 11200, daysOccupied: 30, daysVacant: 0, collected: 11200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '01', roomType: 'triple', tenantName: 'Gaurav Singh', rent: 8800, daysOccupied: 30, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '02', roomType: 'triple', tenantName: 'Anjali Reddy', rent: 8800, daysOccupied: 30, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '301', bedNumber: '03', roomType: 'triple', tenantName: 'Vikram Raj', rent: 8800, daysOccupied: 30, daysVacant: 0, collected: 8800, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '01', roomType: 'double', tenantName: 'Sara Khan', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '302', bedNumber: '02', roomType: 'double', tenantName: 'Arjun Nair', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '01', roomType: 'double', tenantName: 'Meera Iyer', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
    { roomNumber: '303', bedNumber: '02', roomType: 'double', tenantName: 'Karan Patel', rent: 10200, daysOccupied: 30, daysVacant: 0, collected: 10200, revenueLost: 0, paymentStatus: 'paid' },
  ],
}

const REVENUE_DATA = {
  2024: DATA_2024,
  2025: DATA_2025,
  2026: DATA_2026,
}

// ─── Components ──────────────────────────────────────────────────────────────

function MonthlyCollectionChart({ selectedYear, data }) {
  const monthlyTotals = MONTHS.map(month => {
    const monthData = data[month] || []
    return monthData.reduce((sum, item) => sum + item.collected, 0)
  })
  
  const validData = monthlyTotals.filter(v => v > 0)
  const maxVal = Math.max(...validData, 1) * 1.05

  return (
    <div className="rpt-chart-card">
      <div className="rpt-chart-title">
        <span>Monthly Collection • {selectedYear}</span>
        <span className="sub">
          {selectedYear === 2026 ? 'YTD (Jan-Jun)' : 'Full Year'}
        </span>
      </div>
      <div className="rpt-legend">
        <span><span className="rpt-legend-dot met"></span> Collected</span>
        <span><span className="rpt-legend-dot future"></span> No data yet</span>
      </div>
      <div className="rpt-barchart">
        {monthlyTotals.map((val, i) => {
          const hasData = val > 0
          return (
            <div key={i} className="rpt-bar-col">
              <span className="rpt-bar-value">{hasData ? fmt(val) : '—'}</span>
              <div
                className={`rpt-bar ${hasData ? 'rpt-bar-met' : 'rpt-bar-future'}`}
                style={{ height: hasData ? `${Math.max(4, (val / maxVal) * 100)}%` : '4px' }}
              />
              <span className="rpt-bar-label">{MONTHS[i]}</span>
            </div>
          )
        })}
      </div>
      <div className="rpt-target-section">
        <div className="rpt-target-label">Total collected</div>
        <div className="rpt-target-meta">
          <strong>{fmt(validData.reduce((a,b) => a + b, 0))}</strong>
          <span style={{ color: '#6B7280', marginLeft: 6 }}>
            ({validData.length} months)
          </span>
        </div>
      </div>
    </div>
  )
}

function RevenueTab({ selectedYear, data }) {
  // Calculate totals - count occupied bed-months (across all months)
  let totalCollected = 0
  let totalPotential = 0
  let totalLost = 0
  let occupiedBedMonths = 0
  let totalBedMonths = 0

  Object.values(data).forEach(monthData => {
    monthData.forEach(item => {
      totalCollected += item.collected
      totalPotential += item.rent
      totalLost += item.revenueLost
      totalBedMonths += 1 // Each bed entry is one bed-month
      if (item.tenantName) {
        occupiedBedMonths += 1
      }
    })
  })

  const collectionRate = totalPotential > 0 ? Math.round((totalCollected / totalPotential) * 100) : 0

  return (
    <div className="rpt-section">
      <div className="rpt-tiles">
        <StatTile 
          label="Collected" 
          value={fmt(totalCollected)} 
          sub={`${collectionRate}% collection rate`} 
          accent="#22C55E" 
        />
        <StatTile 
          label="Collection rate" 
          value={`${collectionRate}%`} 
          sub={selectedYear === 2026 ? 'YTD (Jan-Jun)' : 'Full Year'} 
          accent="#6366F1" 
        />
        <StatTile 
          label="Occupied bed-months" 
          value={`${occupiedBedMonths}`} 
          sub={`of ${totalBedMonths} total bed-months`} 
          accent="#22C55E" 
        />
        <StatTile 
          label="Revenue Lost" 
          value={fmt(totalLost)} 
          sub="Due to vacancy & delays" 
          accent="#EF4444" 
        />
      </div>

      <div className="rpt-chart-grid">
        <MonthlyCollectionChart selectedYear={selectedYear} data={data} />
        <div className="rpt-quarter-card rpt-card-accent-left" style={{ borderLeftColor: '#EF4444' }}>
          <div className="rpt-quarter-label">Revenue Lost to Vacancy</div>
          <div className="rpt-quarter-value" style={{ color: '#EF4444' }}>
            {fmt(totalLost)}
          </div>
          <div className="rpt-quarter-sub">
            {selectedYear === 2026 ? 'YTD (Jan-Jun)' : 'Full Year'}
          </div>
        </div>
      </div>

      <RevenueMonthAccordion 
        revenueData={REVENUE_DATA} 
        selectedYear={selectedYear}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════

const OwnerRevenueReport = ({ selectedYear }) => {
  const [tab, setTab] = useState('revenue')
  const yearNum = Number(selectedYear) || 2024
  
  const data = REVENUE_DATA[yearNum] || REVENUE_DATA[2024]

  return (
    <div className="cp-rpt-root" key={yearNum}>
      <div className="cp-rpt-view-tabs">
        {[
          { key: 'revenue', label: 'Revenue' },
          { key: 'delay',   label: 'Delay'   },
        ].map(t => (
          <button
            key={t.key}
            className={`cp-rpt-vtab${tab === t.key ? ' on' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'revenue' && (
        <RevenueTab selectedYear={yearNum} data={data} />
      )}
      {tab === 'delay' && (
        <DelayTab selectedYear={yearNum} />
      )}
    </div>
  )
}

export default OwnerRevenueReport